import React, { ButtonHTMLAttributes, FC } from 'react';
import { connect } from 'react-redux';

import { ReduxState } from 'app/redux/types';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import { Spinner } from 'app/design-system/components/Spinner';
import { useTranslation } from 'app/lang';

import {
  selectBigStakes,
  selectFailedBigStakes,
  selectStakesWithDecisionSize,
} from '../../../selectors/bigStakes';

import dictionary from './Header.dictionary.json';

import styles from './BigStakesBarHeader.module.scss';

// ---

type StoreProps = {
  bigStakesSize: number;
  failedBigStakesSize: number;
  stakesWithDecisionSize: number;
};

type HeaderProps = {
  isOpenBigStakeBar: boolean;
  onToggleBigStakeBar: () => void;
  onCloseBigStakeBar: () => void;
};

const Header: FC<HeaderProps & StoreProps> = ({
  bigStakesSize,
  failedBigStakesSize,
  stakesWithDecisionSize,
  isOpenBigStakeBar,
  onToggleBigStakeBar,
  onCloseBigStakeBar,
}) => {
  const { t } = useTranslation();
  const stakesComplete = (bigStakesSize - stakesWithDecisionSize) === 0;
  const canCloseBigStakeBar = stakesComplete && !isOpenBigStakeBar && failedBigStakesSize === 0;

  const HeaderTag = canCloseBigStakeBar ? 'div' : 'button';
  const headerProps: {
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
    onClick?: () => void;
  } = canCloseBigStakeBar ? {} : {
    type: 'button',
    onClick: onToggleBigStakeBar,
  };

  return (
    <HeaderTag {...headerProps}
               data-test="bigStakesBar-header"
               className={styles.header}>
      <div className={styles.statusContainer}
           data-test="bigStakesBar-statusContainer">
        {stakesComplete ? (
          failedBigStakesSize > 0 ? (
            <>
              <Icon size={16}
                    data-test="bigStakesBar-errorIcon"
                    className={styles.errorFilled}
                    icon="error-filled" />
              <p className={styles.label}
                 data-test="bigStakesBar-errorLabel">
                {t(dictionary.errorLabel, { failedBigStakesSize })}
              </p>
            </>
          ) : (
            <>
              <Icon size={IconSize.MEDIUM}
                    data-test="bigStakesBar-successIcon"
                    className={styles.successFilled}
                    icon="success-filled" />
              <p className={styles.label}
                 data-test="bigStakesBar-successLabel">
                {t(dictionary.successLabel)}
              </p>
            </>
          )
        ) : (
          <>
            <Spinner size={16} />
            <p className={styles.label}
               data-test="bigStakesBar-loadingLabel">
              {t(dictionary.loadingLabel)}
            </p>
            <p className={styles.progress}
               data-test="bigStakesBar-loadingProgress">
              {stakesWithDecisionSize}/{bigStakesSize}
            </p>
          </>
        )}
      </div>

      {canCloseBigStakeBar ? (
        <button type="button"
                data-test="bigStakesBar-closeBigStakeBar"
                className={styles.closeBigStakeBarButton}
                onClick={onCloseBigStakeBar}>
          <Icon icon="close-tiny"
                data-test="bigStakesBar-closeIcon"
                size={IconSize.SMALL} />
        </button>
      ) : (
        <Icon className={styles.toggle}
              data-test="bigStakesBar-toggleIcon"
              icon={isOpenBigStakeBar ? 'chevron-up' : 'chevron-down'} />
      )}
    </HeaderTag>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  failedBigStakesSize: selectFailedBigStakes(state).length,
  bigStakesSize: selectBigStakes(state).size,
  stakesWithDecisionSize: selectStakesWithDecisionSize(state),
});

export default connect(mapStateToProps)(Header);
